import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  Select,
  FormControl,
  FormLabel,
  HStack,
  Badge,
  Spinner,
  Switch,
  Heading,
  VStack,
  IconButton,
} from '@chakra-ui/react';
import { FaPlus, FaUsers, FaEdit } from 'react-icons/fa';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { createUser, getUsers, setUserActive, updateUserByAdmin } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import CustomModal from '../components/Modal';

function displayName(user) {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || '—';
}

function hasSiteLabel(hasSite) {
  if (hasSite === true) return 'دارد';
  if (hasSite === false) return 'ندارد';
  return 'پیش‌فرض';
}

const emptyCreateForm = {
  phone: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'user',
  hasSite: '',
};

export default function Users() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ ...emptyCreateForm });
  const [editForm, setEditForm] = useState({
    phone: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user',
    hasSite: '',
    importSyncStockToGateways: true,
    priceSyncEnabled: false,
    strictCodingMatch: false,
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const theadBg = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.900');

  const { data, isLoading, error } = useQuery(['admin-users'], getUsers);
  const users = data?.data || [];

  const createMutation = useMutation(createUser, {
    onSuccess: () => {
      toast.success('کاربر اضافه شد');
      queryClient.invalidateQueries(['admin-users']);
      setIsModalOpen(false);
      setForm({ ...emptyCreateForm });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'خطا در افزودن کاربر'),
  });

  const updateMutation = useMutation(
    ({ userId, payload }) => updateUserByAdmin(userId, payload),
    {
      onSuccess: (res) => {
        toast.success(res?.message || 'کاربر به‌روز شد');
        queryClient.invalidateQueries(['admin-users']);
        setIsEditOpen(false);
        setEditingUser(null);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'خطا در ویرایش کاربر'),
    }
  );

  const activeMutation = useMutation(
    ({ userId, isActive }) => setUserActive(userId, isActive),
    {
      onSuccess: (res) => {
        toast.success(res?.message || 'وضعیت کاربر به‌روز شد');
        queryClient.invalidateQueries(['admin-users']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'خطا در تغییر وضعیت کاربر'),
    }
  );

  const handleCreate = (e) => {
    e.preventDefault();
    const payload = {
      phone: form.phone,
      password: form.password,
      firstName: form.firstName,
      lastName: form.lastName,
      role: form.role,
    };
    if (form.hasSite === 'true') payload.hasSite = true;
    else if (form.hasSite === 'false') payload.hasSite = false;
    else payload.hasSite = null;
    createMutation.mutate(payload);
  };

  const openEdit = (item) => {
    setEditingUser(item);
    setEditForm({
      phone: item.phone || '',
      password: '',
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      role: item.role === 'superadmin' ? 'superadmin' : 'user',
      hasSite: item.hasSite === true ? 'true' : item.hasSite === false ? 'false' : '',
      importSyncStockToGateways: item.settings?.importSyncStockToGateways !== false,
      priceSyncEnabled: item.settings?.priceSyncEnabled === true,
      strictCodingMatch: item.settings?.strictCodingMatch === true,
    });
    setIsEditOpen(true);
  };

  const handleEdit = (e) => {
    e.preventDefault();
    if (!editingUser) return;
    const payload = {
      phone: editForm.phone,
      firstName: editForm.firstName,
      lastName: editForm.lastName,
      role: editForm.role,
      hasSite:
        editForm.hasSite === 'true' ? true : editForm.hasSite === 'false' ? false : null,
      settings: {
        importSyncStockToGateways: editForm.importSyncStockToGateways,
        priceSyncEnabled: editForm.priceSyncEnabled,
        strictCodingMatch: editForm.strictCodingMatch,
      },
    };
    if (editForm.password) payload.password = editForm.password;
    updateMutation.mutate({ userId: editingUser._id, payload });
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3}>
        <HStack spacing={3}>
          <Flex w="44px" h="44px" borderRadius="xl" bg="blue.50" color="blue.500" align="center" justify="center">
            <FaUsers />
          </Flex>
          <Box>
            <Heading size="md" color={textColor}>کاربران</Heading>
            <Text fontSize="sm" color={mutedColor}>افزودن، ویرایش و فعال/غیرفعال کردن حساب‌ها</Text>
          </Box>
        </HStack>
        <Button
          leftIcon={<FaPlus />}
          colorScheme="blue"
          borderRadius="xl"
          onClick={() => setIsModalOpen(true)}
        >
          کاربر جدید
        </Button>
      </Flex>

      <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} overflow="hidden">
        {isLoading ? (
          <Flex justify="center" py={16}><Spinner /></Flex>
        ) : error ? (
          <Text p={6} color="red.500">{error.response?.data?.message || 'خطا در دریافت کاربران'}</Text>
        ) : (
          <Table size="sm">
            <Thead bg={theadBg}>
              <Tr>
                <Th>نام</Th>
                <Th>شماره</Th>
                <Th>نقش</Th>
                <Th>سایت</Th>
                <Th>وضعیت</Th>
                <Th>عملیات</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((item) => {
                const isSelf = String(item._id) === String(currentUser?._id);
                return (
                  <Tr key={item._id}>
                    <Td color={textColor}>
                      {displayName(item)}
                      {isSelf && <Badge mr={2} colorScheme="blue">شما</Badge>}
                    </Td>
                    <Td dir="ltr" textAlign="right">{item.phone}</Td>
                    <Td>
                      <Badge colorScheme={item.role === 'superadmin' ? 'purple' : 'gray'}>
                        {item.role === 'superadmin' ? 'سوپرادمین' : 'کاربر'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          item.hasSite === true ? 'green' : item.hasSite === false ? 'red' : 'gray'
                        }
                      >
                        {hasSiteLabel(item.hasSite)}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack>
                        <Switch
                          colorScheme="green"
                          isChecked={item.isActive !== false}
                          isDisabled={isSelf || activeMutation.isLoading}
                          onChange={(e) => activeMutation.mutate({ userId: item._id, isActive: e.target.checked })}
                        />
                        <Text fontSize="xs" color={mutedColor}>
                          {item.isActive !== false ? 'فعال' : 'غیرفعال'}
                        </Text>
                      </HStack>
                    </Td>
                    <Td>
                      <IconButton
                        aria-label="ویرایش"
                        icon={<FaEdit />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => openEdit(item)}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      <CustomModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="افزودن کاربر"
        submitText="افزودن"
        isLoading={createMutation.isLoading}
        onSubmit={() => document.getElementById('create-user-form')?.requestSubmit()}
      >
        <form id="create-user-form" onSubmit={handleCreate}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>شماره تلفن</FormLabel>
              <Input bg={inputBg} dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>رمز عبور</FormLabel>
              <Input bg={inputBg} type="password" dir="ltr" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </FormControl>
            <HStack>
              <FormControl>
                <FormLabel>نام</FormLabel>
                <Input bg={inputBg} value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>نام خانوادگی</FormLabel>
                <Input bg={inputBg} value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel>نقش</FormLabel>
              <Select bg={inputBg} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="user">کاربر</option>
                <option value="superadmin">سوپرادمین</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>دارای سایت</FormLabel>
              <Select bg={inputBg} value={form.hasSite} onChange={(e) => setForm({ ...form, hasSite: e.target.value })}>
                <option value="">پیش‌فرض (نمایش منوها)</option>
                <option value="true">دارد</option>
                <option value="false">ندارد</option>
              </Select>
            </FormControl>
          </VStack>
        </form>
      </CustomModal>

      <CustomModal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingUser(null);
        }}
        title="ویرایش کاربر"
        submitText="ذخیره"
        isLoading={updateMutation.isLoading}
        onSubmit={() => document.getElementById('edit-user-form')?.requestSubmit()}
      >
        <form id="edit-user-form" onSubmit={handleEdit}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>شماره تلفن</FormLabel>
              <Input bg={inputBg} dir="ltr" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel>رمز عبور جدید (اختیاری)</FormLabel>
              <Input bg={inputBg} type="password" dir="ltr" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
            </FormControl>
            <HStack>
              <FormControl>
                <FormLabel>نام</FormLabel>
                <Input bg={inputBg} value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>نام خانوادگی</FormLabel>
                <Input bg={inputBg} value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel>نقش</FormLabel>
              <Select bg={inputBg} value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                <option value="user">کاربر</option>
                <option value="superadmin">سوپرادمین</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>دارای سایت</FormLabel>
              <Select bg={inputBg} value={editForm.hasSite} onChange={(e) => setEditForm({ ...editForm, hasSite: e.target.value })}>
                <option value="">پیش‌فرض (نمایش منوها)</option>
                <option value="true">دارد</option>
                <option value="false">ندارد</option>
              </Select>
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>همگام‌سازی موجودی</FormLabel>
              <Switch
                colorScheme="blue"
                isChecked={editForm.importSyncStockToGateways}
                onChange={(e) => setEditForm({ ...editForm, importSyncStockToGateways: e.target.checked })}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>همگام‌سازی قیمت</FormLabel>
              <Switch
                colorScheme="blue"
                isChecked={editForm.priceSyncEnabled}
                onChange={(e) => setEditForm({ ...editForm, priceSyncEnabled: e.target.checked })}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>فقط کدهای ثبت‌شده در کدینگ</FormLabel>
              <Switch
                colorScheme="orange"
                isChecked={editForm.strictCodingMatch}
                onChange={(e) => setEditForm({ ...editForm, strictCodingMatch: e.target.checked })}
              />
            </FormControl>
          </VStack>
        </form>
      </CustomModal>
    </Box>
  );
}
